export default {
  alternateProperties: {
    oneAccount: 'One Account.',
    tagline: 'Everything you need to golf.',
    imageAlt: 'GolfNow, GolfPass and more'
  },
  stepCreateAccount: {
    stepTitle: 'Create your account',
    countryRequiredError: 'Please select a country.',
    firstNameRequiredError: 'Please provide your first name.',
    lastNameRequiredError: 'Please provide your last name.',
    passwordRequiredError: 'Please enter a password.',
    phoneNumberRequiredError: 'Please enter a valid phone number.',
    preferredLanguageRequiredError: 'Please select a preferred language.',
    usernameRequiredError: 'Please provide an email address.',
    emailAlreadyRegisteredError: ({ link }) => (
      <>Sorry, that email address is already registered. {link}</>
    ),
    loginInsteadLinkText: 'Login instead?',
    validEmailError: 'Please enter a valid email address.',
    invalidEmailError: 'This doesn\'t look like a valid email address. Please check it and try again.',
    emailMistakeError: 'Please double-check your email address for mistakes and try again.',
    genericError: 'Sorry, an unknown error occurred. Please try again shortly or contact customer support for immediate assistance.',
    firstNameLabel: 'First Name*',
    lastNameLabel: 'Last Name*',
    emailLabel: 'Email Address',
    phoneNumberLabel: 'Phone Number',
    passwordLabel: 'Create a password*',
    createAccountButton: 'Create Account',
    guestLinkText: 'Continue as guest',
    smsOptInDefaultCopy: ({ termsLink, privacyLink }) => (
      <>I agree to receive offers and news from GolfNow and GolfPass by SMS about products, deals and services. Msg. & data rates may apply. Msg. Freq. may vary T&Cs can be viewed at SMS {termsLink} and {privacyLink}</>
    ),
    smsTermsLinkText: 'Terms and Conditions',
    smsPrivacyLinkText: 'Privacy Policy',
    countryLabel: 'Country*',
    preferredLanguageLabel: 'Preferred Language*',
    agreeToTerms: ({ termsLink, privacyLink }) => (
      <>By creating an account or using our sites or services, I agree to the {termsLink} and acknowledge that I have read the {privacyLink}.</>
    ),
    termsLinkText: 'Terms of Service',
    privacyLinkText: 'Privacy Policy',
    signInQuestion: ({ link }) => (
      <>Already have an account? {link}</>
    ),
    signInLinkText: 'Log in'
  },
  authInitiationRequestView: {
    unableToUpdateSettingsError: 'Sorry, we were unable to update your settings at this time.',
    contactSupport: 'Please contact support for more details.',
    clickHereInfo: ({ link }) => (
      <>{link} to go to your Golf Account account information.</>
    ),
    clickHereLinkText: 'Click here',
    applicationNotFoundError: ({ clientId }) => `We're sorry. This application either doesn't exist or has been disabled. Please call support for assistance, and mention this client_id: "${clientId}"`,
    genericFetchError: 'Invalid status code while fetching application data. Please try again later.',
    misconfiguredApplicationError: 'Sorry, the application that sent you here is either misconfigured, or cannot be found in our system.',
    authorizeMultipleTitle: 'Authorize these applications?',
    authorizeSingleTitle: ({ name }) => (
      <>Authorize <strong>{name}</strong>?</>
    ),
    multipleAppsIntro: ({ orgName }) => (
      <>The following applications by <strong>{orgName}</strong> would like to access your account information.</>
    ),
    managePreferencesInfo: ({ link }) => (
      <>You can always manage access to your data on your {link} page.</>
    ),
    preferencesLinkText: 'Preferences',
    agreeToTerms: ({ termsLink, privacyLink }) => (
      <>I agree to the {termsLink} and {privacyLink}</>
    ),
    termsLinkText: 'Terms of Use',
    privacyLinkText: 'Privacy Policy',
    singleAppIntro: ({ appName, orgName }) => (
      <>The application <strong>{appName}</strong> by <strong>{orgName}</strong> would like to access your account information.</>
    ),
    singleAppAuthorizeInfo: 'By authorizing this application you are agreeing to its terms, conditions and privacy policy.',
    declineButton: 'Decline',
    approveButton: 'Approve'
  },
  stepSignIn: {
    stepTitle: 'Enter your email to continue',
    emailLabel: 'Email Address',
    passwordLabel: 'Password*',
    requiredEmailError: 'Please enter an email address.',
    requiredPasswordError: 'Please enter a password.',
    serverError: (
      <div>
        <div>We're sorry. Our servers are having trouble logging you in right now.</div>
        <div>Please try again later.</div>
      </div>
    ),
    validEmailError: 'Please enter a valid email address.',
    invalidEmailError: 'This doesn\'t look like a valid email address. Please check it and try again.',
    emailMistakeError: 'Please double-check your email address for mistakes and try again.',
    userNotFoundError: (
      <div>
        <div>We couldn't find a user with that address, or the password didn't match.</div>
        <div>Please check your credentials and try again.</div>
      </div>
    ),
    accountLockedError: (
      <div>
        <div>This account has been temporary locked for too many failed login attempts.</div>
        <div>Please try again in a while, or contact customer support for immediate assistance.</div>
      </div>
    ),
    loginButton: 'Login',
    forgotPasswordLinkText: 'Forgot your password?',
    createAccountQuestion: ({ link }) => (
      <>Don't have an account? {link}</>
    ),
    createAccountLinkText: 'Create Account'
  },
  stepValidateEmail: {
    stepTitle: 'Validate your email',
    emailLabel: 'Email Address',
    accountExistsError: 'An account already exists with that email.',
    rateLimitedError: 'A verification email has recently been sent. Please wait a moment before trying to resend.',
    verificationSentToast: 'A verification email has sent.',
    initialInstructions: ({ verifyEmail }) => (
      <>We're sorry we couldn't validate your email automatically.  Enter the email address you are trying to create an account for and click the {verifyEmail} button below.</>
    ),
    verifyEmailButtonLabel: 'Verify Email',
    sendExplanation: 'We will send an email to the address with a link to verify the address and allow the email to be used for account creation.',
    verifyEmailButton: 'Verify Email',
    backToCreateAccount: 'Back to create account',
    sentConfirmation: ({ username }) => (
      <>An email has been set to {username} with a link to validate your email.</>
    ),
    sentExpiryInfo: 'This link will expire in 24 hours, emails may take up to 10 minutes to arrive, and be sure to check your Spam folder.',
    resendButton: 'Resend Email',
    validatedConfirmation: ({ username }) => (
      <>{username} has been verified successfully.</>
    ),
    validatedInstructions: 'Click the back button below to return to the Create Account page.',
    backButton: 'Back'
  },
  stepTwoFactor: {
    stepTitle: 'Enter your 2-factor authentication code',
    twoFactorCodeLabel: 'Two-Factor Code',
    requiredCodeError: 'Error: Please enter your 6-digit code.',
    codeLengthError: 'Error: Your code must be six digits long.',
    userNotFoundError: (
      <div>
        <div>We couldn't find a user with that address, or the password didn't match.</div>
        <div>Please check your credentials and try again.</div>
      </div>
    ),
    accountLockedError: (
      <div>
        <div>This account has been temporary locked for too many failed login attempts.</div>
        <div>Please try again in a while, or contact customer support for immediate assistance.</div>
      </div>
    ),
    codeExpiredError: (
      <div>
        <p>Your 2-FA code is either incorrect or expired.</p>
      </div>
    ),
    continueButton: 'Continue'
  },
  stepEmailEntry: {
    stepTitle: 'Enter your email to continue',
    emailLabel: 'Email Address',
    requiredEmailError: 'Please enter an email address.',
    validEmailError: 'Please enter a valid email address.',
    continueButton: 'Continue'
  },
  stepForgotPassword: {
    stepTitle: 'Enter your email to continue',
    emailLabel: 'Email Address',
    enterEmailInfo: 'Enter your email address and we will send you an email with a link to reset your password. Your reset link will expire in 24 hours.',
    sentInfo: ({ username, reSent }) => (
      <>If there is an account associated with <strong>{username}</strong>, a password reset email will be {reSent ? 're-' : ''}sent.</>
    ),
    sentExpiryInfo: 'Remember, your reset link will expire in 24 hours, emails may take up to 10 minutes to arrive, and be sure to check your Spam folder.',
    sentCreateAccountQuestion: ({ link }) => (
      <>Didn't receive an email? Don't have an account? {link}</>
    ),
    sentCreateAccountLinkText: 'Click here to create one.',
    sendButton: 'Send Email',
    resendButton: 'Resend Email',
    signInQuestion: ({ link }) => (
      <>Already have an account? {link}</>
    ),
    signInLinkText: 'Log in',
    requiredEmailError: 'Please enter an email address.',
    validEmailError: 'Please enter a valid email address.',
    rateLimitedError: ({ isSet }) => `A ${isSet ? 'set password' : 'password reset'} email has recently been sent. Please wait a moment before trying to resend.`,
    invalidEmailError: 'This doesn\'t look like a valid email address. Please check it and try again.',
    emailMistakeError: 'Please double-check your email address for mistakes and try again.',
    serverError: 'We\'re sorry, our servers are having trouble at the moment. Please try again later.',
    resetEmailSentConfirmation: 'Reset email sent.'
  }
};
